/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { HttpRequest } from "@azure/functions";
import type { HttpRequest as HttpRequestV3 } from "@azure/functions-v3";
import { SemConvTrace } from "@dynatrace/opentelemetry-core";
import { propagation, ROOT_CONTEXT, TextMapGetter, trace, Tracer } from "@opentelemetry/api";

import { VERSIONS } from "./version";

// ============================================================================

export function extractContext(triggerType: SemConvTrace.FaasTriggerValues, req?: HttpRequest | HttpRequestV3) {
	if (triggerType === SemConvTrace.FaasTriggerValues.HTTP && req != null) {
		// req instanceof HttpRequest is not safe as the passed request object might not come from the same
		// package (in case of multiple @azure/functions versions) or if Azure runtime passes plain object which looks
		// like HttpRequest but is not created by the HttpRequest constructor.
		if (isAzureHttpRequestHeaders(req.headers)) {
			const headersTextMapGetter: TextMapGetter = {
				get(carrier: typeof req.headers, key: string) {
					return carrier.get(key) ?? undefined;
				},
				keys(carrier: typeof req.headers) {
					const hNames: string[] = [];
					carrier.forEach((value: string, key: string) => hNames.push(key));
					return hNames;
				}
			};
			return propagation.extract(ROOT_CONTEXT, req.headers, headersTextMapGetter);
		}
		return propagation.extract(ROOT_CONTEXT, req.headers);
	}
	return ROOT_CONTEXT;
}

let tracer: Tracer | undefined;

export function getTracer(): Tracer {
	if (tracer == null) {
		tracer = trace.getTracer("@dynatrace/opentelemetry-azure-functions", VERSIONS.fullVersion);
	}
	return tracer;
}

export function _testClearTracer(): void {
	tracer = undefined;
}

export function isAzureHttpRequest(obj: unknown): obj is HttpRequest {
	return isAzureHttpRequestInternal(obj);
}

export function isAzureHttpRequestV3(obj: unknown): obj is HttpRequestV3 {
	return isAzureHttpRequestV3Internal(obj);
}

function isAzureHttpRequestInternal(obj: unknown): obj is HttpRequest {
	if (isObject(obj)) {
		return isAzureHttpRequestHeaders(obj.headers) &&
			typeof(obj.method) === "string" &&
			typeof(obj.url) === "string";
	}

	return false;
}

function isAzureHttpRequestHeaders(maybeHeaders: unknown): maybeHeaders is HttpRequest["headers"] {
	return isObject(maybeHeaders) && typeof(maybeHeaders.get) === "function" &&
				typeof(maybeHeaders.forEach) === "function";
}

function isAzureHttpRequestV3Internal(obj: unknown): obj is HttpRequestV3 {
	return isObject(obj) && isObject(obj.headers);
}

function isObject(maybeObj: unknown): maybeObj is Record<string, unknown> {
	return maybeObj != null && typeof(maybeObj) === "object";
}
